import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CompetenciesCompetenciesListDocument,
  CompetenciesCreateCompetencyForJobRoleDocument,
  CompetenciesCreateCompetencyForJobRoleMutationVariables,
  CompetenciesDataCollectionDocument,
  RoleCompetenciesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CompetencyGroupSelector } from 'modules/competencies/components/CompetencyGroupSelector'

interface Props {
  roleId?: string
  onSuccess: (id: string) => void
  onClose: () => void
}

export const NewCompetencyModal: React.FC<Props> = ({ roleId, onSuccess, onClose }) => {
  const [create] = useMutation(CompetenciesCreateCompetencyForJobRoleDocument, {
    extractorFn: (res) => res.competenciesMutations.createCompetenciesCompetency,
  })

  const form = useForm<CompetenciesCreateCompetencyForJobRoleMutationVariables>({
    defaultValues: {
      name: '',
      description: '',
      competencyGroupId: undefined,
    },
    validate(values) {
      const errors: FormErrors<CompetenciesCreateCompetencyForJobRoleMutationVariables> = {}

      if (values.name == '') {
        errors.name = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await create({
        variables: {
          name: values.name,
          description: values.description,
          competencyGroupId: values.competencyGroupId,
          roleIds: roleId ? [roleId] : [],
        },
        refetchQueries: [
          RoleCompetenciesDocument,
          CompetenciesCompetenciesListDocument,
          CompetenciesDataCollectionDocument,
        ],
      })

      const handled = handleResult(result, (errors) => setErrors(errors))
      if (handled.success && handled.data.competency) {
        app.setFlash({
          type: 'correct',
          message: roleId
            ? i18n.t('competencies_v3.new_competency_modal.created_successfully')
            : i18n.t('competencies_v3.new_competency_wizard.created_successfully'),
        })

        track(trackingEvents.competencies.createCompetency, {
          has_group: Boolean(values.competencyGroupId),
        })
        onSuccess(handled.data.competency.id)
      }

      return handled
    },
  })

  return (
    <Modal
      illustration="window-user"
      title={i18n.t('competencies_v3.new_competency_modal.title')}
      description={roleId ? i18n.t('competencies_v3.new_competency_modal.description') : undefined}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting}>
            {i18n.t('common.create')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <TextField
          label={i18n.t('competencies_v3.new_competency_modal.name_input')}
          value={form.values.name}
          onChange={(value: string) => form.setFieldValue('name', value)}
          mandatory
          error={form.touched.name ? form.errors.name : undefined}
        />

        <CompetencyGroupSelector
          value={form.values.competencyGroupId ?? undefined}
          onChange={(groupId) => form.setFieldValue('competencyGroupId', groupId)}
          error={form.touched.competencyGroupId ? form.errors.competencyGroupId : undefined}
        />

        <TextArea
          label={i18n.t('competencies_v3.new_competency_modal.description_input')}
          value={form.values.description ?? ''}
          onChange={(value) => form.setFieldValue('description', value)}
          minRows={4}
          maxHeight="s240"
          error={form.touched.description ? form.errors.description : undefined}
        />
      </Box>
    </Modal>
  )
}
